import { useEffect, useRef, useState } from 'react';
import type {
  PharosRealtimeNotification,
  RealtimeSubscriptionOptions,
  RealtimeSubscriptionState,
} from '@klareospace/pharos-lib-types';
import { useApiClient } from '../api/api-client-context';
import { createPresenceApi } from './presence.api';
import { useInternalRealtimeContext, useRealtimeContext } from './realtime-context';

// ─── Constants ──────────────────────────────────────────────────────────────

/** Shared `POST` presence endpoint path passed as `presenceUrl` to `useRealtimePresence`. */
export const REALTIME_PRESENCE_URL = '/realtime/presence';

/**
 * Hook to subscribe to realtime events with automatic lifecycle management.
 * Safe to call even when realtime is disabled — returns { isSubscribed: false }.
 */
export function useRealtimeSubscription<TData = unknown>(
  options: RealtimeSubscriptionOptions<TData>,
): RealtimeSubscriptionState {
  const context = useInternalRealtimeContext();
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const handlerRef = useRef(options.handler);
  handlerRef.current = options.handler;

  const isEnabled = context?.isEnabled ?? false;
  const isConnected = context?.isConnected ?? false;
  const service = context?.service ?? null;
  const enabled = options.enabled !== false;
  const shouldSubscribe = isEnabled && isConnected && enabled;

  const resource = options.resource;
  const operation = options.operation;
  const event = options.event;

  useEffect(() => {
    if (!shouldSubscribe || !service) {
      setSubscriptionId(null);
      return;
    }

    const id = service.subscribe({
      event,
      // Cast is a compile-time generic convenience: the runtime shape is already
      // validated by EventDispatcher.matchesFilter before the handler is called.
      // TData is not validated at runtime — callers are responsible for type safety.
      handler: (notification) => handlerRef.current(notification as PharosRealtimeNotification<TData>),
      operation,
      resource,
    });
    setSubscriptionId(id);

    return (): void => {
      service.unsubscribe(id);
      setSubscriptionId(null);
    };
  }, [shouldSubscribe, service, resource, operation, event]);

  return {
    isSubscribed: subscriptionId !== null,
    subscriptionId,
  };
}

interface UseRealtimePresenceOptions {
  /** The API client name to use for presence calls (must match a configured client). */
  apiClientName: string;
  /**
   * Whether this presence signal should be sent (default: true) — pass a caller-owned condition
   * (e.g. a feature flag combined with a permission check) to skip enter/leave calls entirely
   * when the feature gating that condition is inactive. Independent from realtime's own global
   * `isEnabled` state, which always takes precedence.
   */
  enabled?: boolean;
  /** The page identifier sent to POST /realtime/presence (e.g., 'orders-list'). */
  page: string;
  /** The presence endpoint URL (e.g., '/realtime/presence'). */
  presenceUrl: string;
}

/**
 * Hook that signals page presence via POST /realtime/presence.
 * Sends "enter" on mount and "leave" on unmount, but only when realtime is enabled and `enabled`
 * (default true) is not explicitly set to false. Safe to call when realtime is disabled — no
 * calls are made.
 */
export function useRealtimePresence({
  apiClientName,
  enabled = true,
  page,
  presenceUrl,
}: UseRealtimePresenceOptions): void {
  const { isEnabled } = useRealtimeContext();
  const apiClient = useApiClient(apiClientName);

  useEffect(() => {
    if (!isEnabled || !enabled) return;

    const presenceApi = createPresenceApi(apiClient);

    presenceApi.enter(presenceUrl, page).catch((error: unknown) => {
      console.error('[useRealtimePresence] Failed to send presence enter:', error);
    });

    return (): void => {
      presenceApi.leave(presenceUrl, page).catch((error: unknown) => {
        console.error('[useRealtimePresence] Failed to send presence leave:', error);
      });
    };
  }, [isEnabled, enabled, page, presenceUrl, apiClient]);
}
